"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { type UserContext, shouldDisplayRole } from "@/lib/permissions";
import { GamificationChip } from "./GamificationChip";
import NotificationBell from "@/components/NotificationBell";
import EventBell from "@/components/EventBell";
import { DiscordConnectButton } from "@/components/profile/DiscordConnectButton";
import type { MemberSection } from "./MemberSidebar";

interface Props {
    title: string;
    user: UserContext;
    streakDays?: number;
    journeyStage?: string;
    openGigsCount?: number;
    // No `unreadNotices`: NotificationBell fetches and counts its own.
    onLogout: () => void;
    onSelectSection: (s: MemberSection) => void;
    onMobileMenuOpen: () => void;
}

function ProfileDropdown({ user, onLogout, onSelectSection }: { user: UserContext; onLogout: () => void; onSelectSection: (s: MemberSection) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const initials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-[#f0f2f4] transition-colors group"
            >
                {user.profile_photo_url ? (
                    <img src={user.profile_photo_url} alt="avatar"
                        className="w-8 h-8 rounded-full object-cover border-2 border-[#e2e8f0] group-hover:border-[#007a8a] transition-colors" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-[#003d45] text-white flex items-center justify-center text-xs font-black border-2 border-transparent group-hover:border-[#007a8a] transition-colors">
                        {initials}
                    </div>
                )}
                <div className="text-left hidden sm:block">
                    <p className="text-sm font-bold text-[#1a1a1a] leading-tight">{user.first_name} {user.last_name}</p>
                    {shouldDisplayRole(user.role) && (
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">{user.role}</p>
                    )}
                </div>
                <span className={`material-symbols-outlined text-[#999] text-base transition-transform ${open ? "rotate-180" : ""}`}>
                    expand_more
                </span>
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-[#e2e8f0] shadow-xl z-50 overflow-hidden">
                    <div className="bg-[#003d45] px-5 py-4 flex items-center gap-3">
                        {user.profile_photo_url ? (
                            <img src={user.profile_photo_url} alt="avatar"
                                className="w-12 h-12 rounded-full object-cover border-2 border-white/30 flex-shrink-0" />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center text-white font-black text-base flex-shrink-0">
                                {initials}
                            </div>
                        )}
                        <div className="min-w-0">
                            <p className="font-black text-white text-base leading-tight truncate">{user.first_name} {user.last_name}</p>
                            <p className="text-xs text-white/60 truncate mt-0.5">{user.email}</p>
                            {shouldDisplayRole(user.role) && (
                                <span className="inline-block mt-1.5 text-[9px] font-black uppercase tracking-widest bg-white/15 text-white/80 px-2 py-0.5 rounded-full">
                                    {user.role}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="px-5 py-3 border-b border-[#f0f0f0] space-y-1.5">
                        {user.phone && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">phone</span>
                                {user.phone}
                            </div>
                        )}
                        {user.member_since && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">calendar_month</span>
                                Member since {user.member_since}
                            </div>
                        )}
                        {user.member_status && (
                            <div className="flex items-center gap-2 text-sm text-[#555]">
                                <span className="material-symbols-outlined text-[#007a8a] text-[18px]">verified</span>
                                <span className="capitalize">{user.member_status} member</span>
                            </div>
                        )}
                        {!user.phone && !user.member_since && !user.member_status && (
                            <p className="text-xs text-[#aaa] italic">No additional details on file.</p>
                        )}
                    </div>

                    <div className="p-2 space-y-0.5">
                        <DiscordConnectButton variant="row" />
                        <button
                            onClick={() => {
                                setOpen(false);
                                onSelectSection("profile");
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#f0f2f4] transition-colors group text-left"
                        >
                            <span className="material-symbols-outlined text-[#666] text-[18px] group-hover:text-[#003d45]">manage_accounts</span>
                            <span className="text-sm font-semibold text-[#333] group-hover:text-[#003d45]">Edit Profile</span>
                        </button>
                        <div className="h-px bg-[#f0f0f0] mx-2 my-1" />
                        <button
                            onClick={() => { setOpen(false); onLogout(); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-50 transition-colors group text-left"
                        >
                            <span className="material-symbols-outlined text-[#666] text-[18px] group-hover:text-[#ae232d]">logout</span>
                            <span className="text-sm font-semibold text-[#333] group-hover:text-[#ae232d]">Sign Out</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function MemberTopBar({ title, user, streakDays = 0, journeyStage, openGigsCount = 0, onLogout, onSelectSection, onMobileMenuOpen }: Props) {
    const streakLabel = streakDays > 0 ? `${streakDays}-day streak` : "Start a streak";
    const streakIcon = streakDays >= 100 ? "local_fire_department" : streakDays >= 30 ? "local_fire_department" : "local_fire_department";

    return (
        <header className="flex-shrink-0 bg-white border-b border-[#e2e8f0] shadow-sm z-30">
            <div className="flex items-center justify-between px-4 lg:px-6 h-14 gap-3">
                {/* Mobile hamburger */}
                <button
                    onClick={onMobileMenuOpen}
                    className="lg:hidden p-2 rounded-lg hover:bg-[#f0f2f4] transition-colors flex-shrink-0"
                    aria-label="Open menu"
                >
                    <span className="material-symbols-outlined text-[#003d45]">menu</span>
                </button>

                {/* Section title */}
                <h1 className="text-sm font-black text-[#003d45] uppercase tracking-wider flex-shrink-0">
                    {title}
                </h1>

                {/* Gamification chips */}
                <div className="hidden md:flex items-center gap-2 flex-1 justify-center">
                    {streakDays > 0 && (
                        <GamificationChip
                            icon={streakIcon}
                            label={streakLabel}
                            tone="flame"
                            onClick={() => onSelectSection("recognition")}
                        />
                    )}
                    {journeyStage && (
                        <GamificationChip
                            icon="trending_up"
                            label={`Stage: ${journeyStage}`}
                            tone="journey"
                            onClick={() => onSelectSection("home")}
                        />
                    )}
                    {openGigsCount > 0 && (
                        <GamificationChip
                            icon="bolt"
                            label={`${openGigsCount} open gig${openGigsCount !== 1 ? "s" : ""}`}
                            tone="gigs"
                            onClick={() => onSelectSection("work")}
                        />
                    )}
                </div>

                {/* Right: event bell + personal notification bell + profile */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <EventBell onViewAll={() => onSelectSection("notices")} />
                    <NotificationBell onViewAll={() => onSelectSection("notices")} />
                    <DiscordConnectButton variant="pill" />
                    <ProfileDropdown user={user} onLogout={onLogout} onSelectSection={onSelectSection} />
                </div>
            </div>
        </header>
    );
}
